import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Optional } from '@angular/core';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { StorageKey, StorageType } from '../storage-key';

const SSR_TOKEN_KEY = makeStateKey<string>('SSR_TOKEN');

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  tabId: string | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(REQUEST) private request: any,
    private transferState: TransferState
  ) { }

  setItem(key: string, value: any, type: string): void {
    if (isPlatformBrowser(this.platformId)) {
      if (type == 'local') localStorage.setItem(key, value);
      else sessionStorage.setItem(key, value);
    }
  }

  getItem(key: string, type: string): any {
    if (isPlatformBrowser(this.platformId)) {
      if (type == 'local') return localStorage.getItem(key);
      return sessionStorage.getItem(key);
    }
    return null;
  }

  remove(key: string, type: string): void {
    if (isPlatformBrowser(this.platformId)) {
      if (type == 'local')
        localStorage.removeItem(key);
      else sessionStorage.removeItem(key);
    }
  }

  clear(type: string): void {
    if (isPlatformBrowser(this.platformId)) {
      if (type == 'local') localStorage.clear();
      else sessionStorage.clear();
    }
  }

  // New: SSR-aware token getter
  getToken(): string | null {
    // Browser: first check if token was transferred from SSR
    if (isPlatformBrowser(this.platformId)) {
      const transferredToken = this.transferState.get(SSR_TOKEN_KEY, null);
      if (transferredToken) {
        // Store it in localStorage for future use
        try {
          localStorage.setItem('token', transferredToken);
        } catch (e) {
          // localStorage might not be available
        }
        return transferredToken;
      }
      
      // Fallback to localStorage
      try {
        return localStorage.getItem('token') || null;
      } catch {
        return null;
      }
    }

    // Server: parse from request cookies
    if (isPlatformServer(this.platformId)) {
      const cookieHeader: string = this.request?.headers?.cookie || '';
      if (!cookieHeader) {
        return null;
      }
      const match = cookieHeader.match(/(?:^|;\s*)authToken=([^;]+)/) || 
                   cookieHeader.match(/(?:^|;\s*)TOKEN=([^;]+)/);
      if (match && match[1]) {
        try {
          return decodeURIComponent(match[1]);
        } catch {
          return match[1];
        }
      }
      return null;
    }

    return null;
  }

  setTokenWithExpiry(token: string, expirationTime: string | Date): void {
    try {
      const expiry = typeof expirationTime === 'string' 
        ? expirationTime 
        : (expirationTime as Date).toISOString();
      this.setItem(StorageKey.TOKEN, token, StorageType.LOCAL);
      this.setItem(StorageKey.TOKEN_EXPIRATION, expiry, StorageType.LOCAL);
    } catch {
      // Ignore storage failures in non-browser or restricted contexts.
    }
  }

  getTokenExpiry(): Date | null {
    try {
      const expiry = this.getItem(StorageKey.TOKEN_EXPIRATION, StorageType.LOCAL);
      return expiry ? new Date(expiry) : null;
    } catch {
      return null;
    }
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return true;
    }
    const now = new Date();
    return now >= expiry;
  }

  clearToken(): void {
    try {
      this.remove(StorageKey.TOKEN, StorageType.LOCAL);
      this.remove(StorageKey.TOKEN_EXPIRATION, StorageType.LOCAL);
    } catch {
      // Ignore storage failures in non-browser or restricted contexts.
    }
  }
}
